import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'

const OAUTH_URL = import.meta.env.VITE_OAUTH_URL || 'https://oauth.heysalad.app'
const ADMIN_API_URL = import.meta.env.VITE_API_URL || 'https://heysalad-admin.heysalad-o.workers.dev'

export default function Login() {
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [magicLinkSent, setMagicLinkSent] = useState(false)
  const { login } = useAuthStore()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Handle OAuth callback with token
  useEffect(() => {
    const token = searchParams.get('token')
    if (token) {
      handleOAuthCallback(token)
    }
  }, [searchParams])

  const handleOAuthCallback = async (token: string) => {
    setLoading(true)
    setError('')

    try {
      // Validate token and get admin profile from our admin API
      const res = await fetch(`${ADMIN_API_URL}/api/admin/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      const data = await res.json()

      if (data.success && data.data?.admin) {
        const adminData = data.data.admin
        const isSuperAdmin = adminData.role === 'super_admin'
        
        login(token, {
          id: adminData.id,
          email: adminData.email,
          name: adminData.name || adminData.email.split('@')[0],
          role: isSuperAdmin ? 'super_admin' : 'company_admin',
          companyId: data.data.company?.id,
          companyName: data.data.company?.name
        })
        navigate('/')
      } else if (data.success === false && data.error) {
        setError(data.error)
      } else {
        // User authenticated but not an admin - show registration option
        setError('You are not registered as an admin. Contact your company administrator.')
      }
    } catch (err) {
      setError('Authentication failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleSendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) {
      setError('Please enter your email address')
      return
    }

    setError('')
    setLoading(true)

    try {
      // Get the current URL for redirect after OAuth
      const redirectUrl = `${window.location.origin}/login`

      const res = await fetch(`${OAUTH_URL}/api/auth/send-magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, redirectUrl })
      })

      const data = await res.json()

      if (data.success) {
        setMagicLinkSent(true)
      } else {
        setError(data.message || 'Failed to send magic link')
      }
    } catch (err) {
      setError('Network error. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (loading && searchParams.get('token')) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-heysalad-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Signing you in...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-5">
      <div className="card w-full max-w-md p-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://pay.heysalad.cash/HeySalad%20Logo%20Black.png" 
            alt="HeySalad" 
            className="h-12 mx-auto"
          />
          <p className="text-gray-500 mt-2 text-sm">Admin Portal</p>
        </div>

        {magicLinkSent ? (
          /* Magic Link Sent Screen */
          <div className="text-center">
            <div className="text-5xl mb-4">✉️</div>
            <h2 className="text-xl font-semibold mb-2">Check your email</h2>
            <p className="text-gray-600 mb-6">
              We sent a magic link to <span className="font-medium">{email}</span>
            </p>
            <p className="text-gray-500 text-sm mb-6">
              Click the link in the email to sign in. The link expires in 15 minutes.
            </p>
            <button
              onClick={() => setMagicLinkSent(false)}
              className="text-heysalad-500 font-medium hover:underline"
            >
              ← Use a different email
            </button>
          </div>
        ) : (
          /* Email Input Screen */
          <form onSubmit={handleSendMagicLink} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field"
                placeholder="you@company.com"
                required
                autoFocus
              />
              <p className="text-gray-500 text-sm mt-2">
                We'll send you a magic link to sign in
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading ? 'Sending...' : 'Send Magic Link'}
            </button>

            <p className="text-center text-gray-500 text-xs mt-6">
              By signing in, you agree to HeySalad's Terms of Service and Privacy Policy
            </p>
          </form>
        )}
      </div>
    </div>
  )
}
